import React from "react"
import { useMap } from "@vis.gl/react-google-maps"
import { getAlertFeatures } from "../lib/helpers/map"
import { GroupedAlertEntry } from "../lib/types/alert"
import { useGoogleMapsEffect } from "../lib/hooks/useGoogleMapsEffect"
import Supercluster from "supercluster"
import { AlertsCluster, SUPERCLUSTER_OPTIONS } from "../lib/helpers/clusters"
import { createBBoxFromGoogleMapBounds } from "../lib/helpers/googleMap"
import AlertsMapGoogleSuperclusterMarker from "./AlertsMapGoogleSuperclusterMarker"

interface AlertsMapGoogleSuperclusterProps {
  mapId: string
  groupedAlertsEntries: GroupedAlertEntry[]
  refetchAlerts: () => Promise<any>
  selectedBikeUuid?: string
  setSelectedBikeUuid: (uuid: string) => void
  onDeselectBike: () => void
  selectedAlertUuid?: string
  onDeselectAlert: () => void
}

const AlertsMapGoogleSupercluster: React.FC<AlertsMapGoogleSuperclusterProps> = ({
  mapId,
  groupedAlertsEntries,
  refetchAlerts,
  selectedBikeUuid,
  setSelectedBikeUuid,
  onDeselectBike,
  selectedAlertUuid,
  onDeselectAlert,
}) => {
  const map = useMap(mapId)
  const [superCluster, setSuperCluster] = React.useState<Supercluster>()
  const [currentClusters, setCurrentClusters] = React.useState<AlertsCluster[]>()

  const updateSupecluster = React.useCallback(
    ({ superClusterInstance }: { superClusterInstance: Supercluster | undefined }) => {
      if (!map || !superClusterInstance) return
      const bounds = map?.getBounds()
      const zoom = map?.getZoom()
      if (!bounds || !zoom) return
      const bbox = createBBoxFromGoogleMapBounds(bounds)
      const clusters = superClusterInstance.getClusters([...bbox[0], ...bbox[1]], zoom) as AlertsCluster[]
      setCurrentClusters(clusters)
    },
    [map],
  )

  useGoogleMapsEffect(
    mapId,
    () => {
      const points =
        groupedAlertsEntries
          ?.filter(([_, { bicycle, zone }]) => {
            if (bicycle) return !!bicycle.location
            if (zone) return !!zone.location
            return false
          })
          ?.map(getAlertFeatures) ?? []
      const cluster = new Supercluster({
        ...SUPERCLUSTER_OPTIONS,
        extent: 256,
      })
      cluster.load(points)
      setSuperCluster(cluster)
      updateSupecluster({ superClusterInstance: cluster })
    },
    [groupedAlertsEntries],
  )

  const getAlertClusterKey = React.useCallback((cluster: AlertsCluster) => {
    if (cluster.properties.cluster) return `cluster_${cluster.id}`
    if (cluster.properties.bicycle) return `asset_${cluster.properties.bicycle.bicycle_uuid}`
    if (cluster.properties.zone) return `zone_${cluster.properties.zone.zone_uuid}`
  }, [])

  // update clusters when map is idle
  useGoogleMapsEffect(
    mapId,
    () => {
      if (!superCluster) return
      const listener = google.maps.event.addListener(map!, "idle", () =>
        updateSupecluster({ superClusterInstance: superCluster }),
      )
      return () => {
        listener.remove()
      }
    },
    [superCluster],
  )

  return (
    <>
      {currentClusters?.map((cluster) => {
        const key = getAlertClusterKey(cluster)
        return (
          <AlertsMapGoogleSuperclusterMarker
            key={key}
            mapId={mapId}
            currentCluster={cluster}
            superClusterInstance={superCluster}
            refetchAlerts={refetchAlerts}
            selectedBikeUuid={selectedBikeUuid}
            setSelectedBikeUuid={setSelectedBikeUuid}
            onDeselectBike={onDeselectBike}
            selectedAlertUuid={selectedAlertUuid}
            onDeselectAlert={onDeselectAlert}
          />
        )
      })}
    </>
  )
}

export default AlertsMapGoogleSupercluster
